import { QRCode } from 'react-qr-code';
import { MFAContext } from './context';
import { useContext, useEffect, useState } from 'react';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';

import { paths } from '@common/types';
import { OTPInput } from '@components';
import useUserStore from 'store/user.store';
import { postRequest } from '@config/axios/axios.config';
import { HttpErrorMessage } from '../common';

export const MFAScan = () => {
  const { goToNextStep, goToFirstStep, mfaType } = useContext(MFAContext);
  const [otpValue, setOTPValue] = useState<string>('');
  const [hasError, setHasError] = useState<boolean>(false);
  const [mfaLink, setMFALink] = useState<string>('');
  // const [mfaManualCode, setMFAManualCode] = useState<string>('');

  const { registerToken } = useUserStore();

  const handleOnChangeOtpValue = (newValue: string) => {
    setOTPValue(newValue);
  };

  const registerMFARequest = async () => {
    const response = await postRequest<
      paths['/auth/register/mfa']['post']['requestBody']['content']['application/json'],
      paths['/auth/register/mfa']['post']['responses']['201']['content']['application/json']
    >('/auth/register/mfa', {
      token: registerToken,
      type: mfaType,
    });

    if (response.data.url) {
      setMFALink(response.data.url);
    }
  };

  useEffect(() => {
    registerMFARequest();
  }, []);

  useEffect(() => {
    async function fetchMyAPI() {
      try {
        setHasError(false);

        await postRequest<
          paths['/auth/register/mfa/confirm']['post']['requestBody']['content']['application/json'],
          paths['/auth/register/mfa/confirm']['post']['responses']['200']['content']['application/json']
        >('/auth/register/mfa/confirm', {
          token: registerToken,
          secret: otpValue,
        });

        goToNextStep();
      } catch {
        setHasError(true);
      }
    }

    if (otpValue.length === 6 && !otpValue.includes(' ')) {
      fetchMyAPI();
    }
  }, [otpValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        width: '28rem',
        height: '100%',
      }}
    >
      <Paper
        elevation={8}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '1.5rem',
          //   paddingBlock: '3rem',
          gap: '16px',
          width: '100%',
          borderRadius: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            padding: 0,
          }}
        >
          <VerifiedUserIcon sx={{ fontSize: '3rem' }} color='primary' />
          <Typography
            sx={{ fontWeight: '600', fontSize: '1.5rem', color: 'primary' }}
          >
            Protect your account with MFA
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ paddingBottom: '16px' }}>Scan QR code:</Typography>
          <QRCode
            size={256}
            style={{ height: 'auto', maxWidth: '100%', width: '60%' }}
            value={mfaLink}
            viewBox={`0 0 128 128`}
          />
          {/* <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 'regular',
              paddingY: '16px',
              textAlign: 'center' }}
          >
            If you cannot scan the QR code, you can manually enter the code
            below into your authenticator application.
          </Typography>
          <Chip label={mfaManualCode} sx={{ borderRadius: 'inherit' }} /> */}
        </Box>

        <Divider sx={{ margin: '0.3rem', width: '100%' }} />

        <Typography
          sx={{
            fontSize: '1rem',
            fontWeight: 'regular',
            textAlign: 'center',
          }}
        >
          Enter the one-time password shown in authentication app to verify your
          account.
        </Typography>

        {hasError && (
          <HttpErrorMessage
            message={
              'The MFA code you entered is invalid. Please check your authenticator app or SMS and try again.'
            }
          />
        )}

        <OTPInput
          value={otpValue}
          onChange={handleOnChangeOtpValue}
          length={6}
        />

        <Button
          variant='text'
          sx={{ padding: '0.5rem', fontWeight: '500', mt: '0.5rem' }}
          onClick={goToFirstStep}
          fullWidth
        >
          SELECT OTHER AUTHENTICATION METHOD
        </Button>
      </Paper>
    </Box>
  );
};
